import { Fragment } from 'react';

import { match, P } from 'ts-pattern';

import { type CheckboxesListOption, CheckboxList } from './checkbox-list';
import { CheckboxListWithOther } from './checkbox-list-with-other';

export type CheckboxGroup = {
    label?: string;
    options: CheckboxesListOption[];
    values: string[];
    onValuesChange: (values: string[]) => void;
    otherValue?: string;
    onOtherValueChange?: (value: string | undefined) => void;
};

type Props = {
    groups: CheckboxGroup[];
    columns?: 1 | 2;
    showOnlySelected?: boolean;
};

export function CheckboxGroupList({ groups, columns = 2, showOnlySelected }: Props) {
    const renderGroup = (group: CheckboxGroup, opts: { noWrapper?: boolean; columns?: 1 | 2 }) =>
        match(group)
            .with({ onOtherValueChange: P.nonNullable }, (g) => (
                <CheckboxListWithOther
                    noWrapper={opts.noWrapper}
                    options={g.options}
                    values={g.values}
                    onValuesChange={g.onValuesChange}
                    otherValue={g.otherValue}
                    onOtherValueChange={g.onOtherValueChange}
                    columns={opts.columns}
                    showOnlySelected={showOnlySelected}
                />
            ))
            .otherwise((g) => (
                <CheckboxList
                    noWrapper={opts.noWrapper}
                    options={g.options}
                    values={g.values}
                    onValuesChange={g.onValuesChange}
                    columns={opts.columns}
                    showOnlySelected={showOnlySelected}
                />
            ));

    return match(!!showOnlySelected)
        .with(true, () => (
            <div className="flex flex-col gap-1">
                {groups.map((group, idx) => (
                    <Fragment key={group.label ?? idx}>
                        {renderGroup(group, { noWrapper: true })}
                    </Fragment>
                ))}
            </div>
        ))
        .otherwise(() => (
            <div className="flex flex-col gap-7">
                {groups.map((group, idx) => (
                    <div key={group.label ?? idx} className="flex flex-col gap-3">
                        {group.label && (
                            <p className="text-muted-foreground text-xs font-black uppercase tracking-wide">
                                {group.label}
                            </p>
                        )}
                        {renderGroup(group, { columns })}
                    </div>
                ))}
            </div>
        ));
}
